<script setup lang="ts">
import BootstrapCard from '@/components/bootstrap/BootstrapCard.vue';
import SubmitButtonContent from '@/components/devextreme/SubmitButtonContent.vue';
import type { IPasswordChangeRequest } from '@/types/interfaces/AccountTypes';
import Icons from '@/constants/Icons';
import { useAccountService } from '@/services/api/accountService';
import {
  DxButtonItem,
  DxButtonOptions,
  DxColCountByScreen,
  DxCompareRule,
  DxCustomRule,
  DxForm,
  DxGroupItem,
  DxRequiredRule,
  DxSimpleItem
} from 'devextreme-vue/form';
import { ref, shallowRef } from 'vue';

definePage({ name: 'AccountPassword', meta: { title: 'account.tabs.password' } });

const account = useAccountService();
const passwordFormRef = shallowRef<DxForm>();
const loading = ref<boolean>(false);
const password = ref<IPasswordChangeRequest>({
  currentPassword: '',
  password: '',
  passwordConfirmation: ''
});

async function onSubmit(): Promise<void> {
  loading.value = true;
  // After a refusal the typed values stay, so the field the warning points at can be corrected.
  if (await account.updatePassword(password.value)) {
    passwordFormRef.value?.instance?.reset();
  }
  loading.value = false;
}
</script>

<template>
  <h5>{{ $t('account.tabs.password') }}</h5>
  <BootstrapCard class="mb-2" :header-text="$t('account.changePassword')">
    <form @submit.prevent="onSubmit">
      <DxForm ref="passwordFormRef" :disabled="loading" :form-data="password" label-mode="floating">
        <DxSimpleItem :editor-options="{ mode: 'password' }" data-field="currentPassword">
          <DxRequiredRule :message="$t('validation.required', { field: $t('fields.currentPassword') })" />
        </DxSimpleItem>
        <DxGroupItem>
          <DxColCountByScreen :lg="2" :md="1" />
          <DxSimpleItem :editor-options="{ mode: 'password' }" data-field="password">
            <DxRequiredRule :message="$t('validation.required', { field: $t('fields.newPassword') })" />
            <DxCustomRule :reevaluate="true"
                :validation-callback="(e: { value: string }) => e.value !== password.currentPassword"
                :message="$t('account.newPasswordSameAsOld')" />
          </DxSimpleItem>
          <DxSimpleItem :editor-options="{ mode: 'password' }" data-field="passwordConfirmation">
            <DxRequiredRule :message="$t('validation.required', { field: $t('fields.newPasswordConfirmation') })" />
            <DxCompareRule :comparison-target="() => password.password" :message="$t('validation.doesNotMatch', { field: $t('fields.newPasswordConfirmation') })" />
          </DxSimpleItem>
        </DxGroupItem>

        <DxButtonItem :horizontal-alignment="'left'">
          <DxButtonOptions :use-submit-behavior="true" template="changePasswordButtonTemplate"
              type="primary" />
        </DxButtonItem>

        <template #changePasswordButtonTemplate>
          <SubmitButtonContent :icon="Icons.FloppyDisk" :loading="loading" :text="$t('account.changePassword')" />
        </template>
      </DxForm>
    </form>
  </BootstrapCard>
</template>

<style scoped>

</style>